/**
 * useChat Hook - Public API for global chat
 *
 * This is the primary way to interact with the chat system from anywhere in the app.
 * Use this hook instead of accessing the store directly.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { openChat } = useChat();
 *
 *   const handleMessage = () => {
 *     openChat({
 *       jobAssignmentId: 123,
 *       entity: {
 *         id: 1,
 *         type: 'company',
 *         name: 'Acme Corp',
 *         profileImageUrl: 'https://...',
 *       },
 *       jobInfo: {
 *         jobName: 'Event Staff',
 *         address: '123 Main St',
 *       },
 *     });
 *   };
 *
 *   return <button onClick={handleMessage}>Message</button>;
 * }
 * ```
 */

import { useCallback, useMemo } from 'react';
import { useChatStore, combineAndSortMessages } from '@stores/chatStore';
import { useUserContext } from '@state/userContext';
import { ChatTarget, CombinedMessage } from '@th-types/chat.type';
import { ChatContentV2 } from '@components/Chat/v2';

export interface UseChatReturn {
  // State
  /** Whether the chat drawer is open */
  isOpen: boolean;
  /** Whether messages are being loaded */
  isLoading: boolean;
  /** Whether a message is being sent */
  isSending: boolean;
  /** Current error message, if any */
  error: string | null;
  /** Whether sending messages is allowed */
  isSendingAllowed: boolean;
  /** The currently active chat target */
  activeChat: ChatTarget | null;
  /** Combined and sorted messages (regular + system) */
  messages: CombinedMessage[];

  // Actions
  /** Open chat drawer with the specified target */
  openChat: (target: ChatTarget) => Promise<void>;
  /** Close the chat drawer */
  closeChat: () => void;
  /** Load chat for embedded mode (doesn't open drawer) */
  loadChat: (target: ChatTarget) => Promise<void>;
  /** Send a message */
  sendMessage: (text: string) => Promise<boolean>;
  /** Clear current error */
  clearError: () => void;

  // Component for embedded usage
  /** ChatContent component for embedded mode */
  ChatContent: typeof ChatContentV2;
}

/**
 * Hook to access and control the global chat system
 */
export function useChat(): UseChatReturn {
  // Get current user ID for optimistic message display
  const { id: currentUserId } = useUserContext();

  // Get state
  const isOpen = useChatStore((state) => state.isOpen);
  const isLoading = useChatStore((state) => state.isLoading);
  const isSending = useChatStore((state) => state.isSending);
  const error = useChatStore((state) => state.error);
  const isSendingAllowed = useChatStore((state) => state.isSendingMsgAllowed);
  const activeChat = useChatStore((state) => state.activeChat);
  const rawMessages = useChatStore((state) => state.messages);
  const systemMessages = useChatStore((state) => state.systemMessages);

  // Combine messages (memoized to avoid unnecessary re-renders)
  const messages = useMemo(
    () => combineAndSortMessages(rawMessages, systemMessages),
    [rawMessages, systemMessages]
  );

  // Get actions
  const storeOpenChat = useChatStore((state) => state.openChat);
  const storeCloseChat = useChatStore((state) => state.closeChat);
  const storeLoadChat = useChatStore((state) => state.loadChat);
  const storeSendMessage = useChatStore((state) => state.sendMessage);
  const storeClearError = useChatStore((state) => state.clearError);

  // Wrap actions with useCallback for stability
  // Pass currentUserId for optimistic message display ("sent by me" alignment)
  const openChat = useCallback(
    async (target: ChatTarget) => {
      await storeOpenChat(target, currentUserId);
    },
    [storeOpenChat, currentUserId]
  );

  const closeChat = useCallback(() => {
    storeCloseChat();
  }, [storeCloseChat]);

  const loadChat = useCallback(
    async (target: ChatTarget) => {
      await storeLoadChat(target, currentUserId);
    },
    [storeLoadChat, currentUserId]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      return storeSendMessage(text);
    },
    [storeSendMessage]
  );

  const clearError = useCallback(() => {
    storeClearError();
  }, [storeClearError]);

  return {
    // State
    isOpen,
    isLoading,
    isSending,
    error,
    isSendingAllowed,
    activeChat,
    messages,

    // Actions
    openChat,
    closeChat,
    loadChat,
    sendMessage,
    clearError,

    // Component
    ChatContent: ChatContentV2,
  };
}

export default useChat;
